import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandingLogo } from "@/components/branding/BrandingLogo";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { API_BASE } from "@/lib/apiClient";
import { isAppUser } from "@/lib/acl";
import type { CurrentUser } from "@/lib/auth";
import { useBranding } from "@/lib/branding/useBranding";

function groupsForAppAccess(user: CurrentUser): string[] {
  const real = user.groups_real;
  if (Array.isArray(real) && real.length > 0) return real;
  return user.groups ?? [];
}

function apiUrl(path: string): string {
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

export function NoAppAccessPage() {
  const { siteName } = useBranding();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"checking" | "notice">("checking");
  const [hasSession, setHasSession] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl("/me"), { credentials: "include", cache: "no-store" });
        if (!res.ok) {
          if (!cancelled) setPhase("notice");
          return;
        }
        const user = (await res.json()) as CurrentUser;
        if (isAppUser(groupsForAppAccess(user))) {
          navigate("/", { replace: true });
          return;
        }
        if (!cancelled) {
          setHasSession(true);
          setPhase("notice");
        }
      } catch {
        if (!cancelled) setPhase("notice");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function leave() {
    setLeaving(true);
    try {
      await fetch(apiUrl("/auth/logout"), { method: "POST", credentials: "include" });
    } catch {
      /* Hinweis bleibt auch ohne erfolgreichen Logout erreichbar. */
    }
    window.location.replace("/login");
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-grouped px-4 py-8 text-label">
      <main className="w-full max-w-[440px] rounded-ios-lg bg-card p-8 shadow-ios-md sm:p-10">
        <header className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 items-center justify-center">
            <BrandingLogo
              imgClassName="max-h-14 max-w-[12rem]"
              fallbackClassName="flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-2xl"
            />
          </div>
          <h1 className="text-xl font-semibold text-label">Kein Zugriff</h1>
        </header>

        {phase === "checking" ? (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-label-secondary">
            <Spinner />
            <span>Berechtigung wird geprüft …</span>
          </div>
        ) : (
          <div className="space-y-5 text-center">
            <p className="text-sm leading-relaxed text-label-secondary">
              Sie sind nicht berechtigt, {siteName} zu verwenden. Die Anmeldung ist nur für Mitglieder
              der Gruppe <strong className="font-medium text-label">App-Nutzer</strong> möglich.
            </p>
            <p className="text-sm leading-relaxed text-label-secondary">
              Bitte wenden Sie sich an Ihre Administration, wenn Sie Zugriff benötigen.
            </p>
            <Button type="button" className="w-full" size="lg" loading={leaving} onClick={() => void leave()}>
              {hasSession ? "Abmelden" : "Zur Anmeldung"}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
